import type { AccorGeoLocations } from '@accor/core-services/src/pos'
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'
import { getDefaultSelectedValues, PosCountryLanguage } from '@accor/core-services/src/pos'
import { env } from '@accor/customer-core/env'

export type SelectedGeolocationItem = {
  lang: string
  language: string
  country: string
}

export type SelectedGeolocationDropdown = {
  selectedOption: string
  continent: string
}

export type SelectedGeolocation = {
  geolocationItem: SelectedGeolocationItem
  geolocationDropdown: SelectedGeolocationDropdown
}

export type SelectedActionData = [
  string,
  {
    attributes: [
      {
        'data-continent': string
        'data-contribzone': string
        'data-country': string
        'data-lang': string
        lang: string
      }
    ]
    continent: string
    contribzone: string
    country: string
    countryCode: string
    defaultUrl: string
    lang: string
    languages: string
    originalText: string
    text: string
    value: string
  }
]

function getGeolocationService() {
  const geolocationService = PosCountryLanguage.getInstance()
  geolocationService.apiKey = `${env.config.apiKey}`
  geolocationService.apiEntryPoint = `${env.config.apiAccorComApi}/referential/v1/pos`
  return geolocationService
}

export async function getSelectedGeolocation(): Promise<SelectedGeolocation> {
  try {
    const selectedGeolocation = await getDefaultSelectedValues()

    const geolocationItem = {
      lang: selectedGeolocation.lang.toUpperCase(),
      language: selectedGeolocation.langLabel,
      country: selectedGeolocation.country
    }

    const geolocationDropdown = {
      selectedOption: selectedGeolocation.selectedOption,
      continent: selectedGeolocation.continent
    }

    return { geolocationItem, geolocationDropdown }
  } catch (error) {
    console.error('An error occurred while getting selected geolocation:', error)
    throw error
  }
}

export async function getAccorGeolocations(): Promise<AccorGeoLocations> {
  try {
    const geolocationService = getGeolocationService()
    return await geolocationService.getAccorCountryLanguage()
  } catch (error) {
    console.error('An error occurred while getting AccorCountryLanguage: ', error)
    return { continents: [], geolocOptionsData: [] }
  }
}

export async function updateGeolocation(
  selectedData: SelectedActionData,
  route: RouteLocationNormalizedLoaded,
  router: Router
): Promise<void | undefined> {
  try {
    const geolocationService = getGeolocationService()
    const updatedGeolocation = await geolocationService.updateGeoloc(
      selectedData[1].country,
      selectedData[1].lang
    )

    const geolocation: any = {
      locale: updatedGeolocation.languageCode,
      currency: updatedGeolocation.currencyCode,
      pos: {
        countryCode: updatedGeolocation.isoCountry[0],
        displayZone: updatedGeolocation.accorCountryCode,
        contribZone: updatedGeolocation.pointOfSaleCode,
        aemVariant: updatedGeolocation.accorCountryCode
      },
      user: {
        lang: updatedGeolocation.languageCode
      }
    }

    if (geolocation) {
      if (route.params.locale && route.params.locale !== geolocation.locale) {
        const to = { ...route, params: { ...route.params, locale: geolocation.locale } }
        await router.push(to)
      }
      window.location.reload()
    }
  } catch (error) {
    console.error('An error occurred while updating geolocation:', error)
    return undefined
  }
}
